//  MUI
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { accountsActions, accountsTypes } from 'app-components/flux/accounts/index';
//  DATA
import type { institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { preferencesActions } from 'app-components/flux/preferences';
import { profileActions, profileSelectors } from 'app-components/flux/profile';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { useFetchOnce } from 'app-components/hooks/utils';
import {
  showAccountLockoutDialog,
  showInitiateTransferDialog,
  showMicrodespositDialog,
} from 'components/Dialogs/A2A/actions';
import type { onboardA2ADialogIndex } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
import { onboardA2ADialogIndexEnums } from 'components/Dialogs/A2A/onboardA2ADialogIndexEnums';
//  CUSTOM COMPONENTS
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import type { FundingAccount } from 'data/fundingAccounts/types';
import * as fundingAccountUtils from 'data/fundingAccounts/utils';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import useKickoffA2AFlow from 'hooks/useKickoffA2AFlow';
//  MAIN
import { Map as ImmutableMap } from 'immutable';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Stack } from '@mui/material';
import QIllustration from './QIllustration';

const useStyles = makeStyles()((theme) => ({
  transferWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  dismissButton: {
    marginRight: 16,
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  wrapper: {
    width: '100%',
    // height: 78,
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    '&.error': {
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.background.paper,
    },
  },
  leftText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));
type A2AEntryButtonProps = {
  institutionLogin: institutionLoginsTypes.InstitutionLogin;
  account: accountsTypes.Account;
  hideOnVerified?: boolean;
  onlyShowVerified?: boolean;
  disableDefaultStyling?: boolean;
};
function A2AEntryButton(props: A2AEntryButtonProps) {
  const { classes, cx } = useStyles();
  const dispatch = useAppDispatch();
  useFetchOnce(profileActions.getProfile, profileSelectors);
  const { institutionLogin, account, hideOnVerified, onlyShowVerified, disableDefaultStyling } = props;
  const kickoffA2AFlow = useKickoffA2AFlow();
  const a2aVisibility = useAppSelector((state) =>
    fundingAccountSelectors.getA2ATransactionsBannerVisibility(state, account, hideOnVerified, onlyShowVerified),
  );
  const {
    showSetupEligible,
    showSetupDepositPending,
    showSetupBanner,
    showFailureBanner,
    showVerifiedButton,
    showAny,
  } = a2aVisibility;
  const fundingAccountsByAccountId = useAppSelector(fundingAccountSelectors.getAllFundingAccountsByAccountId);
  const myFundingAccount: FundingAccount | undefined = fundingAccountsByAccountId.get(account?.id || '');
  const depositOnly = fundingAccountUtils.isAccountA2aDepositOnly(account); // Show unique text for deposit only

  const dismissButtonClick = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-account',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Dismiss Transfers',
              content:
                'You won’t see this again for this account. To set up transfers for this or other accounts in the future, go to Settings > Accounts.',
              confirmLabel: 'Dismiss',
              denyLabel: 'Cancel',
              size: 'xs',
              tertiaryLabelVariant: 'text',
              tertiaryLabel: 'Dismiss for all accounts',
              tertiaryAction: () => {
                dispatch(
                  preferencesActions.setPreference({
                    section: 'shared',
                    group: 'dataset',
                    preference: { a2a: { hideAllTransactionBanners: true } },
                  }),
                );
              },
              confirmAction: () =>
                dispatch(
                  accountsActions.updateAccount(
                    accountsTypes.mkUpdateAccount(account?.type, { id: account?.id, isA2ABannerDismissed: true }),
                    {
                      undo: { userMessage: 'Banner dismissed' },
                    },
                  ),
                ),
            }),
          ),
        }),
      ),
    );
  };
  const buttonClick = () => {
    if (showSetupEligible) {
      kickoffA2AFlow({
        initiator: 'transactions',
        startTabIndex: onboardA2ADialogIndexEnums.ACCT_INFO as onboardA2ADialogIndex,
        initialInstitutionId: institutionLogin?.institutionId || '',
        initialAccountId: account?.id || '',
      });
    } else if (showSetupDepositPending) {
      dispatch(
        showMicrodespositDialog({
          fundingAccountId: myFundingAccount?.id,
          hideAccountPicker: true,
          initiator: 'transactions',
        }),
      );
    } else if (showVerifiedButton) {
      dispatch(
        showInitiateTransferDialog({
          initiator: 'transactions',
          transferOptions: {
            fromAccount: account,
          },
        }),
      );
    } else if (showFailureBanner) {
      dispatch(
        showAccountLockoutDialog({
          fundingAccountId: myFundingAccount?.id,
        }),
      );
    }
  };
  return (
    showAny &&
    account && (
      <>
        {showSetupBanner && (
          <Paper className={disableDefaultStyling ? '' : classes.wrapper}>
            <Stack gap={2} direction='row' alignItems='center'>
              <QIllustration name='transferCards' size='xs' />
              {/* <CompareArrowsRoundedIcon className={classes.titleIcon} /> */}
              <div className={classes.leftText}>
                <Typography variant='paragraphMedium' color='text.primary'>
                  {showSetupEligible && 'Move money directly in Simplifi'}
                  {showSetupDepositPending && 'Verify this account for transfers'}
                </Typography>
                <Typography variant='paragraphRegular' color='text.secondary'>
                  {showSetupEligible && depositOnly && 'Set up this account now to start receiving funds!'}
                  {showSetupEligible && !depositOnly && 'Start transferring funds with this account now.'}
                  {showSetupDepositPending && 'Confirm the two small deposit amounts we recently sent to this account.'}
                </Typography>
              </div>
            </Stack>
            <div className={classes.buttonWrapper}>
              <Button
                size='small'
                className={classes.dismissButton}
                id='a2a-dismiss-button'
                onClick={dismissButtonClick}
                variant='text'
              >
                Dismiss
              </Button>
              <Button
                size='small'
                id='a2a-begin-button'
                onClick={buttonClick}
                disabled={!showSetupEligible && !showSetupDepositPending && !showVerifiedButton}
                variant={showSetupDepositPending ? 'contained' : 'outlined'}
              >
                {showSetupEligible && 'Set up transfers'}
                {showSetupDepositPending && 'Verify'}
              </Button>
            </div>
          </Paper>
        )}
        {showFailureBanner && (
          <Paper className={cx(disableDefaultStyling ? '' : classes.wrapper, 'error')}>
            <div className={classes.leftText}>
              <Typography variant='paragraphMedium' color='text.primary'>
                Account verification failed
              </Typography>
              <Typography variant='paragraphRegular' color='text.secondary'>
                We had trouble delivering verification deposits to this account.
              </Typography>
            </div>
            <Button size='small' id='a2a-begin-button' onClick={buttonClick} variant='contained'>
              View
            </Button>
          </Paper>
        )}
        {showVerifiedButton && (
          <div className={disableDefaultStyling ? '' : classes.transferWrapper}>
            <Button
              size='small'
              id='a2a-begin-button'
              onClick={buttonClick}
              startIcon={<CompareArrowsRoundedIcon />}
              variant='outlined'
            >
              Transfer Money
            </Button>
          </div>
        )}
      </>
    )
  );
}
export default A2AEntryButton;
